import { ProvenanceExpandableAttachments } from '@/components/provenance/ProvenanceExpandableAttachments';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useSpeak2MeStore } from '@/store/speak2meStore';
import { Check, ChevronDown, ChevronUp, Loader2, Paperclip, Send, Upload, Volume2, X } from 'lucide-react';
import { VoiceModeIcon } from '@/components/ui/voice-mode-icon';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import type { ComposerDictation, VoiceSessionError, VoiceSessionStatus } from '../useVoiceSession';
import { ChatComposerMicButton } from './ChatComposerMicButton';
import { ChatComposerModelSelect } from './ChatComposerModelSelect';
import { ChatComposerWaveform } from './ChatComposerWaveform';

// The composer shell. Every evidence tool mounted in the chat wears it, so the action
// row lands on the same pixel whichever tool is active.
export const COMPOSER_SHELL_CLASS = 'border-t px-3 pt-2 pb-3';

export interface StagedAttachment {
  fileId: number;
  name: string;
}

interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  canSend: boolean;
  disabled?: boolean;
  placeholder?: string;
  voiceEnabled: boolean;
  dictation: ComposerDictation;
  isConversationActive: boolean;
  voiceStatus: VoiceSessionStatus;
  voiceError: VoiceSessionError;
  isUploadingFiles?: boolean;
  onConversationToggle: () => void;
  onAttachFiles: (files: File[]) => void | Promise<void>;
  stagedAttachments: StagedAttachment[];
  onRemoveStagedAttachment: (fileId: number) => void;
  neoChatId?: number;
  switchSlot?: ReactNode;
}

function useAutosizeTextarea(
  ref: RefObject<HTMLTextAreaElement>,
  value: string,
  maxLines = 5
) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    el.style.height = '0px';
    const cs = window.getComputedStyle(el);
    const lh = parseFloat(cs.lineHeight);
    const safeLh = Number.isFinite(lh) ? lh : 24;
    const pt = parseFloat(cs.paddingTop) || 0;
    const pb = parseFloat(cs.paddingBottom) || 0;
    const maxHeight = safeLh * maxLines + pt + pb;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [ref, value, maxLines]);
}

export function ChatComposer({
  value,
  onChange,
  onSend,
  canSend,
  disabled = false,
  placeholder = 'Speak to me!',
  voiceEnabled,
  dictation,
  isConversationActive,
  voiceStatus,
  voiceError,
  isUploadingFiles = false,
  onConversationToggle,
  onAttachFiles,
  stagedAttachments,
  onRemoveStagedAttachment,
  neoChatId,
  switchSlot,
}: ChatComposerProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [isDropTargetActive, setIsDropTargetActive] = useState(false);
  const [isStagedExpanded, setIsStagedExpanded] = useState(false);
  const dropDepthRef = useRef(0);
  const view = useSpeak2MeStore((state) => state.view);
  const showAttachments = view !== 'unread-list';

  useAutosizeTextarea(textareaRef, value);

  useEffect(() => {
    if (stagedAttachments.length === 0) {
      setIsStagedExpanded(false);
    }
  }, [stagedAttachments.length]);

  const handleAttachSelection = (fileList: FileList | null) => {
    if (!fileList || disabled || isUploadingFiles) {
      return;
    }
    const files = Array.from(fileList).filter((file) => file.size > 0);
    if (files.length === 0) {
      return;
    }
    void onAttachFiles(files);
  };

  const disableFileActions = disabled || isUploadingFiles;

  const voiceToggleLabel = !voiceEnabled
    ? 'Voice unavailable'
    : isConversationActive
      ? 'Stop voice mode'
      : voiceError
        ? 'Microphone access denied — check browser permissions'
        : 'Start voice mode';

  const voiceToggleIcon = isConversationActive ? (
    voiceStatus === 'speaking' ? (
      <Volume2 className="h-3.5 w-3.5 animate-pulse" />
    ) : voiceStatus === 'suspended' ? (
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
    ) : (
      <VoiceModeIcon className="h-3.5 w-3.5" active />
    )
  ) : (
    <VoiceModeIcon className="h-3.5 w-3.5" />
  );

  return (
    <TooltipProvider>
      <div
        data-testid="chat-composer"
        className={`${COMPOSER_SHELL_CLASS} ${isDropTargetActive ? 'bg-primary/5' : ''}`}
        onDragEnter={(event) => {
          event.preventDefault();
          dropDepthRef.current += 1;
          if (!disableFileActions && event.dataTransfer.types.includes('Files')) {
            setIsDropTargetActive(true);
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disableFileActions && event.dataTransfer.types.includes('Files')) {
            setIsDropTargetActive(true);
          }
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          dropDepthRef.current = Math.max(0, dropDepthRef.current - 1);
          if (dropDepthRef.current === 0) {
            setIsDropTargetActive(false);
          }
        }}
        onDrop={(event) => {
          event.preventDefault();
          dropDepthRef.current = 0;
          setIsDropTargetActive(false);
          if (disableFileActions) {
            return;
          }
          handleAttachSelection(event.dataTransfer.files);
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(event) => {
            handleAttachSelection(event.target.files);
            event.currentTarget.value = '';
          }}
        />

        {showAttachments && neoChatId !== undefined ? (
          <ProvenanceExpandableAttachments parentContentId={neoChatId} />
        ) : null}

        <div>
          {stagedAttachments.length > 0 ? (
            <div
              data-testid="chat-composer-staged-attachments"
              className="mb-1 border-b px-1 pb-1.5 pt-1"
            >
              {stagedAttachments.length === 1 ? (
                <div className="flex flex-wrap gap-1">
                  {stagedAttachments.map((attachment) => (
                    <StagedAttachmentChip
                      key={attachment.fileId}
                      attachment={attachment}
                      disabled={disabled}
                      onRemove={onRemoveStagedAttachment}
                    />
                  ))}
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    data-testid="chat-composer-staged-attachments-toggle"
                    onClick={() => setIsStagedExpanded((prev) => !prev)}
                    aria-expanded={isStagedExpanded}
                    className="flex h-7 w-full items-center gap-1.5 rounded-sm px-2 text-xs text-muted-foreground hover:bg-accent"
                  >
                    <Paperclip className="h-3 w-3" />
                    <span className="flex-1 text-left">
                      {stagedAttachments.length} files attached
                    </span>
                    {isStagedExpanded ? (
                      <ChevronUp className="h-3 w-3" />
                    ) : (
                      <ChevronDown className="h-3 w-3" />
                    )}
                  </button>
                  {isStagedExpanded ? (
                    <div
                      data-testid="chat-composer-staged-attachments-list"
                      className="mt-1 flex max-h-[8.75rem] flex-wrap gap-1 overflow-y-auto scrollbar-thin border-t pt-1"
                    >
                      {stagedAttachments.map((attachment) => (
                        <StagedAttachmentChip
                          key={attachment.fileId}
                          attachment={attachment}
                          disabled={disabled}
                          onRemove={onRemoveStagedAttachment}
                        />
                      ))}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          ) : null}

          <textarea
            data-testid="chat-composer-input"
            ref={textareaRef}
            rows={1}
            className="block w-full resize-none border-0 bg-transparent px-1 py-1.5 text-sm leading-6 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 overflow-y-auto scrollbar-thin disabled:cursor-not-allowed disabled:opacity-50"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                if (canSend && !disabled) onSend();
              }
            }}
            placeholder={placeholder}
            disabled={disabled}
          />

          {dictation.active ? (
            <div className="flex items-center gap-1 pt-1">
              <div className="min-w-0 flex-1 px-1">
                <ChatComposerWaveform analyser={dictation.analyser} />
              </div>
              <Button
                data-testid="chat-composer-dictate-cancel"
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                disabled={dictation.finalizing}
                onClick={dictation.cancel}
                title="Discard dictation"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
              <Button
                data-testid="chat-composer-dictate-accept"
                type="button"
                size="icon"
                className="h-7 w-7"
                disabled={dictation.finalizing}
                onClick={dictation.accept}
                title="Finish dictation"
              >
                {dictation.finalizing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Check className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-1 pt-1">
              <ChatComposerModelSelect disabled={disabled} />
              <div className="flex-1" />

              {switchSlot}

              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      disabled={disableFileActions || isConversationActive}
                      onClick={() => fileInputRef.current?.click()}
                      title="Attach files"
                    >
                      {isUploadingFiles ? (
                        <Upload className="h-3.5 w-3.5 animate-pulse" />
                      ) : (
                        <Paperclip className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent className="mr-2" sideOffset={4}>
                  <p>{isUploadingFiles ? 'Uploading files...' : 'Attach files'}</p>
                </TooltipContent>
              </Tooltip>

              {!isConversationActive ? (
                <ChatComposerMicButton dictation={dictation} disabled={disabled} />
              ) : null}

              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button
                      data-testid="chat-composer-voice-toggle"
                      type="button"
                      variant={isConversationActive ? 'default' : 'ghost'}
                      size={isConversationActive ? 'sm' : 'icon'}
                      className={isConversationActive ? 'h-7 gap-1 px-2' : 'h-7 w-7'}
                      disabled={!voiceEnabled || (!isConversationActive && disabled)}
                      onClick={onConversationToggle}
                      title={voiceToggleLabel}
                    >
                      {isConversationActive ? <span className="text-xs">Stop</span> : null}
                      {voiceToggleIcon}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent className="mr-2" sideOffset={4}>
                  <p>{voiceToggleLabel}</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button
                      data-testid="chat-composer-submit"
                      type="button"
                      variant="neo"
                      size="icon"
                      className="h-7 w-7"
                      disabled={!canSend || disabled}
                      onClick={onSend}
                      title="Send message"
                    >
                      <Send className="h-3.5 w-3.5" />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent className="mr-2" sideOffset={4}>
                  <p>Send message</p>
                </TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>

        {isDropTargetActive && !disableFileActions ? (
          <p className="mt-1 text-xs text-muted-foreground">Drop files to upload and continue.</p>
        ) : null}
      </div>
    </TooltipProvider>
  );
}

interface StagedAttachmentChipProps {
  attachment: StagedAttachment;
  disabled?: boolean;
  onRemove: (fileId: number) => void;
}

function StagedAttachmentChip({ attachment, disabled, onRemove }: StagedAttachmentChipProps) {
  return (
    <span
      data-testid="chat-composer-staged-attachment"
      className="inline-flex max-w-full items-center gap-1 rounded-md border bg-muted/50 py-0.5 pl-2 pr-0.5 text-xs"
    >
      <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="truncate">{attachment.name}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-4 w-4 shrink-0"
        disabled={disabled}
        onClick={() => onRemove(attachment.fileId)}
        aria-label={`Remove ${attachment.name}`}
      >
        <X className="h-3 w-3" />
      </Button>
    </span>
  );
}
